import React from "react";
import {
    SocialAccount,
    SocialAccountRestControllerApiFactory,
    SocialConnectRestControllerApiFactory,
} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {Alert, Grid} from "@mui/material";
import {FormModel, useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {types} from "../../../../components/form/types";
import {FormButtonProps} from "../../../../components/form/form_inputs/form_button";

/** Prefijo del externalId de una cuenta de LinkedIn conectada como perfil personal. */
const URN_PERSONA = "urn:li:person:";

export const SocialAccountEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(SocialAccountRestControllerApiFactory);

    const form: FormModel<SocialAccount> = useForm({
        fetcher: (id) => ctrl.getSocialAccount(id),
        updater: (bean) => ctrl.saveSocialAccount(bean),
        remover: (id) => ctrl.deleteOrDismissSocialAccount(id),
        factory: () => ({} as SocialAccount),
        extraActions: [
            {
                label: "Reconectar",
                disabled: () => !form.propModel.value?.provider,
                action: () => reconnect(),
            } as FormButtonProps,
            {
                label: "Comprobar token",
                disabled: () => !form.propModel.value?.id,
                action: () => verify(),
            } as FormButtonProps,
        ],
    } as UseFormProps<SocialAccount>);

    const bean = form.propModel.value;
    const isLinkedin = bean?.provider === "LINKEDIN";

    /**
     * En LinkedIn el modo se deduce del externalId: los scopes de perfil y página son
     * excluyentes, así que reconectar en el modo equivocado dejaría un token incapaz de publicar
     * donde toca. En Meta no aplica (una sola conexión para Facebook e Instagram).
     */
    const asMemberOf = (acc?: SocialAccount) =>
        acc?.provider === "LINKEDIN" ? (acc?.externalId ?? "").startsWith(URN_PERSONA) : undefined;

    /**
     * Rehace el OAuth de esta cuenta, con su proveedor y su modo. Meta comparte una única
     * conexión para Facebook e Instagram, así que reconectar desde cualquiera de las dos rehace
     * ambas (lo resuelve el callback del backend).
     */
    const reconnect = () => {
        const acc = form.propModel.value;          // al pulsar, no al renderizar
        if (!acc?.provider) {
            return;
        }
        const actions = apiManager.factory(SocialConnectRestControllerApiFactory);
        apiManager.execute(actions.connect(acc.provider, asMemberOf(acc))).then((res) => {
            const authUrl = res?.["authUrl"];
            if (!authUrl) {
                apiManager.enqueueSnackbar("No se obtuvo la URL de autorización", {variant: "warning"});
                return;
            }
            window.open(authUrl, "_blank", "noopener");
            apiManager.enqueueSnackbar(
                "Autoriza en la ventana que se ha abierto y luego recarga esta página para ver el estado.",
                {variant: "info"});
        });
    };

    /** Pregunta a la red si el token sigue vivo y refresca la ficha con el estado resultante. */
    const verify = () => {
        const actions = apiManager.factory(SocialConnectRestControllerApiFactory);
        apiManager.execute(actions.verifyTokens()).then((checks) => {
            const mine = (checks ?? []).find((c) => c.accountId === form.getIdOrErr());
            if (mine) {
                apiManager.enqueueSnackbar(
                    mine.valid ? "Token válido" : `Token NO válido: ${mine.detail ?? "sin detalle"}`,
                    {variant: mine.valid ? "success" : "error"});
            }
            apiManager.executeRaw(ctrl.getSocialAccount(form.getIdOrErr()))
                .then((r) => form.setter(r.data));
        });
    };

    return (
        <FormContent form={form} title={"Cuenta social"}>
            {bean?.status === "EXPIRED" && (
                <Alert severity="error" sx={{mb: 2}}>
                    Esta cuenta no puede publicar: el token ya no es válido. Pulsa
                    <strong> Reconectar</strong> para rehacer la autorización.
                    {isLinkedin
                        ? ` Se reconectará como ${asMemberOf(bean) ? "perfil personal" : "página de empresa"}.`
                        : " Meta reconecta Facebook e Instagram a la vez."}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormSelect field={"provider"} label={"Red"} type={types.socialProviders}/>
                </Grid>
                <Grid item xs={12} sm={5}>
                    <FormInput field={"name"} label={"Nombre de la cuenta/página"}/>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormSelect field={"status"} label={"Estado"} type={types.socialAccountStatuses}/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"externalId"} label={"Id externo (pageId / IG id / URN)"}/>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormInput field={"expiresAt"} label={"Caducidad del token"} type="datetime"/>
                </Grid>
            </Grid>

            <FormInput field={"scopes"} label={"Scopes concedidos"} rows={2}/>
            <FormInput field={"observation"} label={"Observaciones"} rows={2}/>
        </FormContent>
    );
};
